import dayjs from 'dayjs'

import { BuilderComponentSettingValue } from '@makyoapp/makyo-common'
import * as Icon from '@makyoapp/makyo-icon/icon'
import { Chip, SelectDropdown } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useUpdateCompSettings, useUpdateCompSettingValue } from '@/hooks/useUpdateCompSettings'
import { BuilderComponentSetting, ComponentSettingKey, FlattenedItem } from '@/shared/builder'
import { hasColorAdaptiveSettings, hasResponsiveSettings } from '@/shared/builder/viewport/responsive-settings'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'

interface SelectFieldSettingProps {
  id: string
  selectSettings: BuilderComponentSetting['selectSettings']
  ancestors: FlattenedItem[]
  settingId: string
  value: any
}

const SelectFieldSetting = ({ selectSettings, ancestors, settingId, id, value }: SelectFieldSettingProps) => {
  const { setShowDateTimeValidationModal } = useAppBuilderContext()
  const { activeComponent, activeLayerClient } = useActiveLayerContext()
  const updateSettings = useUpdateCompSettings()
  const updateValue = useUpdateCompSettingValue(id)

  const { multiple = false, options = [], multiline = false, type = 'dropdown' } = selectSettings ?? {}

  const rawOptions = (typeof options === 'function' ? options(activeComponent?.id || '', ancestors) : options) || []
  const isArr = Array.isArray(value)

  const getDropdownValue = () => {
    const valueArray = value as BuilderComponentSettingValue[]

    if (!multiple)
      return rawOptions?.find(
        (opt: { value: string | number; icon?: string | undefined; label: string }) => opt?.value === value
      )

    if (isArr)
      return valueArray.map((v: BuilderComponentSettingValue) =>
        rawOptions?.find((o: { value: string | number; icon?: string | undefined; label: string }) => o.value === v)
      )

    return []
  }

  const onChipClick = (opt: { value: string | number; icon?: string | undefined; label: string }) => {
    const updated = value.includes(opt.value)
      ? value.filter((v: string | number) => v !== opt.value)
      : [...value, opt.value]

    if (hasResponsiveSettings(id)) {
      updateValue(updated)
    } else {
      updateSettings((prevSettings) => {
        prevSettings[id] = updated
      })
    }
  }

  if (type === 'chip' && multiple) {
    return (
      <div className='flex flex-wrap gap-2'>
        {rawOptions.map((opt) => {
          const isSelected = (value ?? []).includes(opt.value)
          return (
            <Chip
              key={opt.value}
              outlined
              active={isSelected}
              onClick={() => onChipClick(opt)}
              className='cursor-pointer text-m-m'
            >
              <span>{opt.label}</span>
              {isSelected && <Icon.Check16 width={14} height={14} />}
            </Chip>
          )
        })}
      </div>
    )
  }

  return (
    <SelectDropdown
      withSearch={rawOptions.length > SHOW_SEARCH_LIMIT}
      id={settingId}
      value={
        getDropdownValue() as {
          value: string
          icon?: string
          label: string
        }
      }
      onChange={async (opt) => {
        let newValue = multiple ? (Array.isArray(opt) ? opt.map((o) => o?.value) : []) : opt?.value

        if (id === ComponentSettingKey.DateTimePickerBehaviorEndTime) {
          const endTime = newValue as string
          const parsedEndTime = dayjs(endTime, 'HH:mm')
          const component = activeLayerClient?.componentsMap.get(activeComponent?.id || '')
          const startTime = component?.settings?.behavior?.custom?.[
            ComponentSettingKey.DateTimePickerBehaviorStartTime
          ] as string
          const parsedStartTime = dayjs(startTime, 'HH:mm')

          if (parsedEndTime.isBefore(parsedStartTime)) {
            setShowDateTimeValidationModal(true)
            newValue = '24:00'
          }
        }

        if (id === ComponentSettingKey.DateTimePickerBehaviorStartTime) {
          const startTime = newValue as string
          const parsedStartTime = dayjs(startTime, 'HH:mm')
          const component = activeLayerClient?.componentsMap.get(activeComponent?.id || '')
          const endTime = component?.settings?.behavior?.custom?.[
            ComponentSettingKey.DateTimePickerBehaviorEndTime
          ] as string
          const parsedEndTime = dayjs(endTime, 'HH:mm')

          if (parsedStartTime.isAfter(parsedEndTime)) {
            setShowDateTimeValidationModal(true)
            newValue = '00:00'
          }
        }

        if (hasResponsiveSettings(id) || hasColorAdaptiveSettings(id)) {
          await updateValue(newValue)
        } else {
          await updateSettings((prevSettings) => {
            prevSettings[id] = newValue
          })
        }
      }}
      optionLabel={(opt) => opt?.label}
      renderOption={(opt) => {
        const MaybeIc = opt?.icon ? Icon[opt.icon as keyof typeof Icon] : undefined
        const maybeImg = opt?.icon && !MaybeIc && <img src={opt.icon} width={24} height={24} alt='' />
        const ic = MaybeIc ? <MaybeIc width={16} height={16} /> : maybeImg
        return (
          <div className='flex-c'>
            {ic}
            {multiline ? (
              <div>
                <div className='text-m-sm text-light'>{opt?.label}</div>
                <div className='line-clamp-3'>{opt?.value}</div>
              </div>
            ) : (
              <div>{opt?.label}</div>
            )}
          </div>
        )
      }}
      multiple={multiple}
      options={rawOptions || []}
    />
  )
}

export default SelectFieldSetting
